"use client";
import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import trueCircle from "@/public/images/trueCircle.svg";
import infoCircle from "@/public/images/infoCircle.svg";
import Image from "next/image";
import { useTranslate } from "@/components/base/providers/TranslateProv";

interface AdmissionRequirementsProps {
  item: any;
}

const AdmissionRequirements: React.FC<AdmissionRequirementsProps> = ({
  item,
}) => {
  const { locale } = useTranslate() as { locale: "ar" | "en" };

  const validRequirements = item?.admission_requirement.filter(
    (parentRequirement: any) =>
      parentRequirement.name[locale] &&
      parentRequirement.requirements &&
      parentRequirement.requirements[locale] &&
      parentRequirement.requirements[locale]?.length > 0
  );

  if (!validRequirements || validRequirements.length === 0) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-center mb-6">
        {locale === "ar"
          ? `شروط القبول لـ ${item.name.ar}`
          : `Admission requirements for ${item.name.en}`}
      </h2>
      <Accordion
        type="single"
        collapsible
        className="w-full"
        defaultValue="item-1"
      >
        {validRequirements.map((parentRequirement: any, index: number) => (
          <AccordionItem
            key={parentRequirement.id}
            value={`item-${index + 1}`}
            className="mb-5 p-5 border border-[#EBEBEB] rounded-[15px]"
          >
            <AccordionTrigger className="text-[16px] md:text-25 text-textDark font-[500] border-b border-b-[#EBEBEB] mb-5">
              {parentRequirement.name[locale]}
            </AccordionTrigger>
            <AccordionContent className="rounded-[15px]   bg-white border border-[#EBEBEB] p-5 shadow-[0px_4px_54px_rgba(0,0,0,0.05)]">
              {parentRequirement.requirements[locale]?.map(
                (requirement: any, idx: number) => (
                  <div
                    key={idx}
                    className={`flex items-start gap-3 mb-3 relative ${
                      requirement.body && requirement.body.length > 0
                        ? "before:absolute before:w-[2px] before:h-[80%] before:bg-primaryColor before:bottom-[15px] before:z-0 before:start-[8px] overflow-hidden"
                        : ""
                    }`}
                  >
                    <Image
                      src={trueCircle}
                      alt="check"
                      width={24}
                      height={24}
                      className="flex-shrink-0 z-[1] w-[24px] h-[24px]  "
                    />
                    <div>
                      {requirement.title && (
                        <p className="text-16 text-[#6C7278] mb-1">
                          {requirement.title}
                        </p>
                      )}
                      {requirement.body && (
                        <ul className="list-none pl-0">
                          {requirement.body.map(
                            (item: any, bodyIdx: number) => (
                              <li
                                key={bodyIdx}
                                className="flex items-start gap-3 text-sm text-gray-600 mb-1"
                              >
                                <div
                                  className={`relative h-[24px] before:absolute before:w-7 before:h-[2px] before:top-1/2 before:-translate-y-1/2 before:bg-primaryColor ${
                                    locale === "ar"
                                      ? "before:left-[12px]"
                                      : "before:right-[12px]"
                                  }
                                  `}
                                >
                                  <Image
                                    src={trueCircle}
                                    alt="check"
                                    width={14}
                                    height={14}
                                    className="mt-1 flex-shrink-0"
                                  />
                                </div>
                                {item}
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                )
              )}
              {parentRequirement.custom_content?.[locale] && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-4 flex items-start gap-3">
                  <Image
                    src={infoCircle}
                    alt="info"
                    width={20}
                    height={20}
                    className="mt-1 flex-shrink-0"
                  />
                  <p
                    className="text-sm text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: parentRequirement.custom_content[locale],
                    }}
                  ></p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default AdmissionRequirements;
